/**
 * @file EventBus.ts
 * @role Typed publish/subscribe for cross-cutting NOTIFICATIONS (audio, analytics,
 *       orientation changes, error surfaced). Decouples notifiers from listeners.
 * @owns Listener registration and synchronous dispatch.
 * @reads GameEventMap.
 * @neverDoes Carry game-flow control or backend calls in listeners. A listener that
 *            drives the spin lifecycle is a bug — that belongs in the state machine.
 * @invariants See AGENT_PROTOCOL "EventBus = notifications only".
 * @see docs/AGENT_PROTOCOL.md
 */
import type { GameEventMap, GameEventName } from './EventTypes';

type Handler<K extends GameEventName> = (payload: GameEventMap[K]) => void;

export class EventBus {
  private readonly handlers = new Map<GameEventName, Set<Handler<GameEventName>>>();

  /** Subscribe. Returns an unsubscribe function. */
  on<K extends GameEventName>(type: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler<GameEventName>);
    return () => this.off(type, handler);
  }

  off<K extends GameEventName>(type: K, handler: Handler<K>): void {
    this.handlers.get(type)?.delete(handler as Handler<GameEventName>);
  }

  emit<K extends GameEventName>(
    type: K,
    ...payload: GameEventMap[K] extends void ? [] : [GameEventMap[K]]
  ): void {
    const set = this.handlers.get(type);
    if (!set) return;
    const value = payload[0] as GameEventMap[K];
    // Iterate a copy so a handler unsubscribing during dispatch can't skip another.
    for (const handler of [...set]) {
      handler(value);
    }
  }

  /** Remove all listeners. Call on teardown to avoid leaks. */
  clear(): void {
    this.handlers.clear();
  }
}
